import React, { useEffect, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import styles from "./equipmentWeekGrid.module.css";
import moment from "moment";
import { Flex, Button } from "antd";
import { Text } from "ui/Typography";
import { useTranslation } from "react-i18next";
import API from "constants/api";
import EventBlock from "./EventBlock";
import { getEventColor, getEventBorderColor, getJournalIcon } from "pagesV2/pro/Calendar/utils";
import BookingDetailsModal from "pagesV2/pro/clients/ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/Modals/BookingDetails";
import { useRefreshCalendarBookings } from "pagesV2/pro/Calendar/useRefreshCalendarBookings";
import { useCalendarJournalNoteDates } from "pagesV2/pro/Calendar/hooks/useCalendarSelectors";
import Journal from "pagesV2/pro/Calendar/Modals/Journal";
import IconWithBg from "ui/IconWithBg";
import useActiveGuide from "hooks/useActiveClient";
import { usePermissions } from "hooks/usePermissions";
import { MODULES, ACTIONS } from "utils/permissions";

const EQUIPMENT_API = `${API.BASE_URL}/guide/boats/listing?lang=en-GB`;

const isEventForEquipmentAndDay = (ev, eq, day) => {
  if (!moment(ev.date).isSame(day, "day")) return false
  const assigned = ev.assigned_equipment || []
  return assigned.some((item) => item.name === eq.name || item.boat_id === eq.id)
}

const formatEquipmentEventLabels = (ev) => {
  const startMoment = ev.start ? moment(ev.start, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null
  const endMoment = ev.end ? moment(ev.end, ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]) : null
  return {
    ...ev,
    startLabel: startMoment?.isValid() ? startMoment.format("h:mm A") : ev.start,
    endLabel: endMoment?.isValid() ? endMoment.format("h:mm A") : ev.end,
  }
}

const buildEquipmentDayMap = (eq, calendarDays, events) => {
  const dayMap = {}
  calendarDays.forEach((day) => {
    const dayStr = day.format("YYYY-MM-DD")
    dayMap[dayStr] = events.filter((ev) => isEventForEquipmentAndDay(ev, eq, day)).map(formatEquipmentEventLabels)
  })
  return dayMap
}

// EquipmentWeekGrid: Equipment as rows, days as columns, table layout
const EquipmentWeekGrid = ({ calendarDays, events, equipmentIds, categoryIds }) => {
  const [allEquipment, setAllEquipment] = useState([]);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [isJournalModalOpen, setIsJournalModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [visibleCounts, setVisibleCounts] = useState({});
  const { t } = useTranslation();
  const MAX_VISIBLE = 4;

  const getVisibleCount = useCallback((key) => visibleCounts[key] ?? MAX_VISIBLE, [visibleCounts]);
  const showMore = useCallback((key) => setVisibleCounts((prev) => ({ ...prev, [key]: (prev[key] ?? MAX_VISIBLE) + MAX_VISIBLE })), []);
  const showLess = useCallback((key) => setVisibleCounts((prev) => ({ ...prev, [key]: MAX_VISIBLE })), []);
  const journalNoteDates = useCalendarJournalNoteDates();
  const { isActive: active } = useActiveGuide();
  const { hasPermission } = usePermissions();
  const canViewJournal = hasPermission(MODULES.CALENDAR, ACTIONS.VIEW_JOURNAL);
  const canCreateJournal = hasPermission(MODULES.CALENDAR, ACTIONS.CREATE_JOURNAL);
  const canEditJournal = hasPermission(MODULES.CALENDAR, ACTIONS.EDIT_JOURNAL);
  const weekStartDate = calendarDays?.[0]?.format("YYYY-MM-DD");
  const weekEndDate = calendarDays?.at(-1)?.format("YYYY-MM-DD");

  useEffect(() => {
    const token = localStorage.getItem("access_token");
    const fetchOptions = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
    fetch(EQUIPMENT_API, fetchOptions)
      .then((r) => r.json())
      .then((res) => {
        const items = (res.data || []).map((eq) => ({ id: eq.id, name: eq.equipment_title }));
        setAllEquipment(items);
      })
      .catch(() => {});
  }, []);

  const equipmentList = useMemo(() => {
    // Collect equipment IDs from assigned_equipment in events (for category filter)
    const assignedEquipmentIds = new Set();
    if (categoryIds?.length > 0) {
      events.forEach((ev) => {
        (ev.assigned_equipment || []).forEach((item) => {
          if (item.boat_id) assignedEquipmentIds.add(item.boat_id);
          if (item.id) assignedEquipmentIds.add(item.id);
        });
      });
    }

    // Both equipment + category filter — merge
    if (equipmentIds?.length > 0 && categoryIds?.length > 0) {
      const mergedIds = new Set([...equipmentIds, ...assignedEquipmentIds]);
      return allEquipment.filter((eq) => mergedIds.has(eq.id));
    }

    // Equipment filter only
    if (equipmentIds?.length > 0) return allEquipment.filter((eq) => equipmentIds.includes(eq.id));

    // Category filter only — show equipment from assigned events
    if (categoryIds?.length > 0) return allEquipment.filter((eq) => assignedEquipmentIds.has(eq.id));

    // No filter — show all
    return allEquipment;
  }, [allEquipment, equipmentIds, categoryIds, events]);

  const showNameColumn = true;

  // Build lookup: equipment name -> day -> normalized events
  const eventsByEquipmentAndDay = useMemo(() => {
    const map = {};
    equipmentList.forEach((eq) => {
      map[eq.name] = buildEquipmentDayMap(eq, calendarDays, events);
    });
    return map;
  }, [equipmentList, calendarDays, events]);

  const refreshCalendarBookings = useRefreshCalendarBookings()
  const handleBookingCancel = () => {
    setIsBookingModalOpen(false);
    setSelectedBookingId(null);
  };
  const handleBookingCancelSuccess = () => { handleBookingCancel(); refreshCalendarBookings() }

  return (
    <div className={styles.equipmentWeekGrid}>
      <table className={styles.equipmentWeekTable}>
        <thead>
          <tr className={styles.equipmentWeekHeaderRow}>
            {showNameColumn && <th className={styles.equipmentWeekHeaderCell}>Equipment</th>}
            {calendarDays.map((day) => {
              const dayStr = day.format("YYYY-MM-DD");
              const hasJournalNote = journalNoteDates?.includes(dayStr);
              const hasJournalPermission = hasJournalNote ? canViewJournal || canEditJournal : canCreateJournal;
              return (
                <th key={dayStr} className={styles.equipmentWeekHeaderCell}>
                  <Flex vertical align="center" gap={2}>
                    <Text fw="500">{day.format("ddd D")}</Text>
                  </Flex>
                  {hasJournalPermission && (
                    <Flex justify="center" className="mt-xxs">
                      <IconWithBg
                        disabled={!active}
                        icon={getJournalIcon(journalNoteDates, dayStr)}
                        onClick={active ? () => { setSelectedDate(dayStr); setIsJournalModalOpen(true); } : undefined}
                      />
                    </Flex>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className={styles.equipmentWeekBody}>
          {equipmentList.length === 0 ? (
            [0, 1, 2, 3, 4].map((i) => (
              <tr key={`empty-${i}`} className={styles.equipmentWeekRow}>
                {showNameColumn && <td className={styles.equipmentWeekEquipmentCell} />}
                {calendarDays.map((day) => (
                  <td key={day.format("YYYY-MM-DD")} className={styles.equipmentWeekEventCell} />
                ))}
              </tr>
            ))
          ) : (
            <>
              {equipmentList.map((eq) => (
                <tr key={eq.id} className={styles.equipmentWeekRow}>
                  {showNameColumn && (
                    <td className={styles.equipmentWeekEquipmentCell}>
                      <Text>{eq.name}</Text>
                    </td>
                  )}
                  {calendarDays.map((day) => {
                    const dayStr = day.format("YYYY-MM-DD");
                    const cellEvents = eventsByEquipmentAndDay[eq.name]?.[dayStr] || [];
                    const cellKey = `${eq.id}-${dayStr}`;
                    const visibleCount = getVisibleCount(cellKey);
                    const visibleEvents = cellEvents.slice(0, visibleCount);
                    const allVisible = visibleCount >= cellEvents.length;
                    const hasMore = !allVisible && cellEvents.length > MAX_VISIBLE;
                    const canShowLess = allVisible && cellEvents.length > MAX_VISIBLE;
                    return (
                      <td key={dayStr} className={styles.equipmentWeekEventCell}>
                        <div className={styles.equipmentWeekEventCellInner}>
                          {visibleEvents.map((ev) => (
                            <EventBlock
                              key={ev.id ?? (ev.title + ev.start)}
                              event={ev}
                              eventColor={getEventColor(ev.payment_status)}
                              eventBorderColor={getEventBorderColor(ev.payment_status)}
                              setIsBookingModalOpen={setIsBookingModalOpen}
                              setSelectedBookingId={setSelectedBookingId}
                              compact
                            />
                          ))}
                          {hasMore && (
                            <Button type="link" size="small" onClick={() => showMore(cellKey)}>
                              {t("calendar.view_more")}
                            </Button>
                          )}
                          {canShowLess && (
                            <Button type="link" size="small" onClick={() => showLess(cellKey)}>
                              Read less
                            </Button>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {equipmentList.length === 0 && [0, 1, 2, 3].map((i) => (
                <tr key={`placeholder-${i}`} className={styles.equipmentWeekRow}>
                  {showNameColumn && <td className={styles.equipmentWeekEquipmentCell} />}
                  {calendarDays.map((day) => (
                    <td key={day.format("YYYY-MM-DD")} className={styles.equipmentWeekEventCell} />
                  ))}
                </tr>
              ))}
            </>
          )}
        </tbody>
      </table>

      {isBookingModalOpen && (
        <BookingDetailsModal
          open={isBookingModalOpen}
          handleOnCancel={handleBookingCancel}
          bookingUuid={selectedBookingId}
          onCancelSuccess={handleBookingCancelSuccess}
        />
      )}
      {isJournalModalOpen && (
        <Journal
          open={isJournalModalOpen}
          setOpen={setIsJournalModalOpen}
          onCancel={() => setIsJournalModalOpen(false)}
          selectedDate={selectedDate}
          startDate={weekStartDate}
          endDate={weekEndDate}
          range="week"
        />
      )}
    </div>
  );
};

EquipmentWeekGrid.propTypes = {
  calendarDays: PropTypes.array.isRequired,
  events: PropTypes.array.isRequired,
  equipmentIds: PropTypes.array,
  categoryIds: PropTypes.array,
};

export default EquipmentWeekGrid;
